import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { subscribeMonitoringIncomingBump, type MonIncomingBumpItem } from '../monitoring/monitoringIncomingBumpPoll'
import { monitoringNotificationLink, type MonitoringNotificationAddress } from '../monitoring/monitoringNotificationLink'
import './MonitoringPortalIncoming.css'

type PortalToast = {
  id: string
  chat_id: string
  message_id: number
  topic_top_message_id: number | null
  title: string
  preview: string
  sender: string
  timeLabel: string
  hasQuestion: boolean
  team_hub_channel_id: string
}

function formatMsgTime(iso: string): string {
  const s = iso?.trim()
  if (!s) return ''
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function monitoringMainPath(pathname: string): boolean {
  return pathname === '/admin/monitoring_tg' || pathname === '/employee/monitoring_tg'
}

const NOTIFY_DISMISS_KEY = 'ads_mon_tg_os_notify_banner_dismissed_v1'

export default function MonitoringPortalIncoming() {
  const { canAccessTgMonitoring, isAdmin } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const pathRef = useRef(location.pathname)
  pathRef.current = location.pathname

  const [toasts, setToasts] = useState<PortalToast[]>([])
  const [toastTrayOpen, setToastTrayOpen] = useState(false)
  const dedupeRef = useRef<Set<string>>(new Set())
  const [notifyBannerDismissed, setNotifyBannerDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(NOTIFY_DISMISS_KEY) === '1'
    } catch {
      return false
    }
  })

  const dismissNotifyBanner = useCallback(() => {
    setNotifyBannerDismissed(true)
    try {
      sessionStorage.setItem(NOTIFY_DISMISS_KEY, '1')
    } catch {
      /* ignore */
    }
  }, [])

  const openSlackChat = useCallback(
    (item: MonitoringNotificationAddress) => {
      navigate(monitoringNotificationLink(item, isAdmin))
    },
    [isAdmin, navigate],
  )

  const pushPortalToast = useCallback((it: MonIncomingBumpItem) => {
    const cid = String(it.chat_id || '').trim()
    const mid =
      typeof it.message_id === 'number' ? it.message_id : parseInt(String(it.message_id), 10)
    if (!cid || !Number.isFinite(mid) || mid <= 0) return
    const dedupeKey = `${cid}:${mid}`
    if (dedupeRef.current.has(dedupeKey)) return
    if (dedupeRef.current.size > 500) {
      dedupeRef.current = new Set([...dedupeRef.current].slice(-250))
    }
    dedupeRef.current.add(dedupeKey)

    const topicTop =
      it.topic_top_message_id != null && Number(it.topic_top_message_id) > 0
        ? Number(it.topic_top_message_id)
        : null
    const preview = (it.text || '').replace(/\s+/g, ' ').trim().slice(0, 220)
    const rawText = (it.text || '').trim()
    const id = `${dedupeKey}-${Date.now()}`
    const chatLabel = String(it.chat_title || '').trim() || cid
    const toast: PortalToast = {
      id,
      chat_id: cid,
      message_id: mid,
      topic_top_message_id: topicTop,
      title: chatLabel,
      preview: preview || '(без текста)',
      sender: (it.sender_name || '').trim() || '—',
      timeLabel: it.date ? formatMsgTime(String(it.date)) : '',
      hasQuestion: rawText.includes('?'),
      team_hub_channel_id: String(it.team_hub_channel_id || '').trim(),
    }
    setToasts((prev) => [...prev.slice(-18), toast])
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 42000)

    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        const n = new Notification(`Telegram: ${chatLabel}`, {
          body: `${toast.sender}${toast.timeLabel ? ` · ${toast.timeLabel}` : ''}\n${toast.preview}`.slice(0, 240),
          tag: dedupeKey,
        })
        n.onclick = () => {
          try {
            window.focus()
          } catch {
            /* ignore */
          }
          window.location.assign(monitoringNotificationLink(toast, isAdmin))
          n.close()
        }
      } catch {
        /* ignore */
      }
    }
  }, [isAdmin])

  useEffect(() => {
    if (!canAccessTgMonitoring) return
    return subscribeMonitoringIncomingBump((items) => {
      if (monitoringMainPath(pathRef.current)) return
      for (const it of items) {
        pushPortalToast(it)
      }
    })
  }, [canAccessTgMonitoring, pushPortalToast])

  useEffect(() => {
    if (toasts.length === 0) setToastTrayOpen(false)
  }, [toasts.length])

  if (!canAccessTgMonitoring) return null

  const showNotifyHint =
    typeof Notification !== 'undefined' &&
    Notification.permission === 'default' &&
    !notifyBannerDismissed
  const visibleToasts = [...toasts].reverse().slice(0, 8)
  const latestToast = visibleToasts[0] || null

  return (
    <>
      {showNotifyHint ? (
        <div className="pp-mon-notify-banner" role="region" aria-label="Уведомления ОС">
          <div className="pp-mon-notify-banner-inner">
            <span>Входящие в мониторинге: можно включить системные уведомления.</span>
            <button
              type="button"
              className="pp-mon-notify-btn"
              onClick={() => {
                void Notification.requestPermission().finally(() => dismissNotifyBanner())
              }}
            >
              Разрешить
            </button>
            <button type="button" className="pp-mon-notify-btn" onClick={dismissNotifyBanner}>
              Скрыть
            </button>
          </div>
        </div>
      ) : null}

      {latestToast ? (
        <div
          className={`pp-mon-toast-stack${toastTrayOpen ? ' is-open' : ' is-collapsed'}`}
          aria-live="polite"
        >
          {toastTrayOpen ? (
            <section className="pp-mon-toast-tray" aria-label="Новые сообщения мониторинга">
              <header className="pp-mon-toast-tray-head">
                <div>
                  <strong>Monitoring Bot</strong>
                  <span>{toasts.length} новых сообщений</span>
                </div>
                <div className="pp-mon-toast-tray-actions">
                  <button type="button" onClick={() => setToastTrayOpen(false)}>Свернуть</button>
                  <button type="button" onClick={() => setToasts([])}>Очистить</button>
                </div>
              </header>
              <div className="pp-mon-toast-list">
                {visibleToasts.map((t) => (
                  <article
                    key={t.id}
                    className={`pp-mon-toast${t.hasQuestion ? ' pp-mon-toast--question' : ''}`}
                  >
                    <div className="pp-mon-toast-head">
                      <span className="pp-mon-toast-title" title={t.title}>{t.title}</span>
                      <button
                        type="button"
                        className="pp-mon-toast-close"
                        aria-label="Закрыть уведомление"
                        onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
                      >×</button>
                    </div>
                    <p className="pp-mon-toast-meta">
                      {t.sender}
                      {t.timeLabel ? ` · ${t.timeLabel}` : ''}
                      {t.topic_top_message_id != null && t.topic_top_message_id > 0
                        ? ` · тема #${t.topic_top_message_id}`
                        : ''}
                    </p>
                    <p className="pp-mon-toast-preview">{t.preview}</p>
                    <button
                      type="button"
                      className="pp-mon-toast-open"
                      onClick={() => {
                        openSlackChat(t)
                        setToasts((prev) => prev.filter((x) => x.id !== t.id))
                      }}
                    >Открыть чат Monitoring Bot</button>
                  </article>
                ))}
              </div>
            </section>
          ) : (
            <div className="pp-mon-toast-summary">
              <button
                type="button"
                className="pp-mon-toast-count"
                aria-label="Показать все новые сообщения"
                onClick={() => setToastTrayOpen(true)}
              >
                {toasts.length}
              </button>
              <button
                type="button"
                className="pp-mon-toast-summary-open"
                onClick={() => {
                  openSlackChat(latestToast)
                  setToasts((prev) => prev.filter((item) => item.id !== latestToast.id))
                }}
                aria-label={`Открыть чат Monitoring Bot: ${latestToast.title}`}
              >
                <span className="pp-mon-toast-summary-copy">
                  <strong>Monitoring Bot</strong>
                  <small>{latestToast.title} · {latestToast.preview}</small>
                </span>
                <span aria-hidden="true">Открыть чат</span>
              </button>
              <button
                type="button"
                className="pp-mon-toast-summary-close"
                aria-label="Скрыть все уведомления"
                onClick={() => setToasts([])}
              >×</button>
            </div>
          )}
        </div>
      ) : null}
    </>
  )
}
